import ModalHeader from "@/components/ui/ModalHeader";
import ModalLayout from "@/components/layout/ModalLayout";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { SeCompany } from "@/interface/setting/se_company";
import { useState, useEffect } from "react";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Button } from "@/components/ui/Button";
import { useCompanyInfoSave, useCompanyInfoUpdate } from "@/hooks/setting/se_company/useCompanySetting";
import { toast } from "sonner";

interface SettingModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "update";
  initialData?: SeCompany;
}

export default function SettingModal({ isOpen, onClose, mode, initialData }: SettingModalProps) {
  const [formData, setFormData] = useState<SeCompany>({
    id: 0,
    companyCode: "",
    companyName: "",
    ceoName: "",
    bizNo: "",
    zipcode: "",
    address: "",
    bizType: "",
    bizItem: "",
    tel: "",
    mobile: "",
    fax: "",
    remark: "",
    createdDate: "",
    updateDate: "",
  });

  const { data: codeList } = useCodeListByCompanyCode();

  // API 훅들
  const saveMutation = useCompanyInfoSave();
  const updateMutation = useCompanyInfoUpdate();

  // Update 모드일 때 초기 데이터 설정
  useEffect(() => {
    if (mode === "update" && initialData) {
      setFormData(initialData);
    } else if (mode === "add") {
      setFormData({
        // id: 0,
        companyCode: "",
        companyName: "",
        ceoName: "",
        bizNo: "",
        zipcode: "",
        address: "",
        bizType: "",
        bizItem: "",
        tel: "",
        mobile: "",
        fax: "",
        remark: "",
        createdDate: "",
        updateDate: "",
      });
    }
  }, [mode, initialData, isOpen]);

  const handleSave = () => {
    console.log(mode);
    // 필수 필드 검증
    if (!formData.companyCode || !formData.companyName) {
      toast.error("회사코드와 회사명은 필수 입력 항목입니다.");
      return;
    }

    if (mode === "add") {
      // 추가 모드
      saveMutation.mutate(formData, {
        onSuccess: response => {
          toast.success("회사 정보가 성공적으로 추가되었습니다.");
          onClose();
        },
        onError: error => {
          toast.error("회사 정보 추가에 실패했습니다.");
          console.error(error);
        },
      });
    } else {
      // 수정 모드
      if (!formData.id) {
        toast.error("수정할 회사 정보의 ID가 없습니다.");
        return;
      }

      updateMutation.mutate(
        { id: formData.id, data: formData },
        {
          onSuccess: response => {
            toast.success("회사 정보가 성공적으로 수정되었습니다.");
            onClose();
          },
          onError: error => {
            toast.error("회사 정보 수정에 실패했습니다.");
            console.error(error);
          },
        }
      );
    }
  };

  // SelectBox 옵션 변환
  const companyCodeOptions =
    codeList?.map(item => ({
      value: item.code,
      label: item.name,
    })) || [];

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title={mode === "add" ? "회사 추가" : "회사 수정"}>
      <div>
        <ModalHeader title="기본 정보" />
        <div className="flex flex-row gap-3">
          <div className="flex-1">
            <SelectBox
              label="회사코드"
              value={formData.companyCode || ""}
              onChange={value => setFormData({ ...formData, companyCode: value })}
              options={companyCodeOptions}
              placeholder="회사코드를 선택하세요"
              disabled={mode === "update"}
              required
            />
          </div>
          <InputBox
            label="회사명"
            wrapperClassName="flex-[3]"
            value={formData.companyName}
            onChange={e => setFormData({ ...formData, companyName: e.target.value })}
            required
          />
        </div>

        <InputBox
          label="비고"
          value={formData.remark || ""}
          onChange={e => setFormData({ ...formData, remark: e.target.value })}
          wrapperClassName="flex-1"
          className="mb-4"
        />

        <ModalHeader title="사업자 정보" />
        <div className="flex flex-row gap-3">
          <InputBox
            label="대표자"
            value={formData.ceoName || ""}
            onChange={e => setFormData({ ...formData, ceoName: e.target.value })}
            wrapperClassName="flex-1"
          />
          <InputBox
            label="사업자등록번호"
            value={formData.bizNo || ""}
            onChange={e => setFormData({ ...formData, bizNo: e.target.value })}
            wrapperClassName="flex-1"
          />
        </div>
        <div className="flex flex-row gap-2">
          <InputBox
            label="우편번호"
            value={formData.zipcode || ""}
            onChange={e => setFormData({ ...formData, zipcode: e.target.value })}
            wrapperClassName="flex-1"
          />
          <InputBox
            label="주소"
            value={formData.address || ""}
            onChange={e => setFormData({ ...formData, address: e.target.value })}
            wrapperClassName="flex-[6]"
          />
        </div>
        <div className="flex flex-row gap-3 mb-4">
          <InputBox
            label="업태"
            value={formData.bizType || ""}
            onChange={e => setFormData({ ...formData, bizType: e.target.value })}
            wrapperClassName="flex-1"
          />
          <InputBox
            label="종목"
            value={formData.bizItem || ""}
            onChange={e => setFormData({ ...formData, bizItem: e.target.value })}
            wrapperClassName="flex-1"
          />
        </div>

        <ModalHeader title="연락처 정보" />
        <div className="flex flex-row gap-3">
          <InputBox
            label="전화번호"
            value={formData.tel || ""}
            onChange={e => setFormData({ ...formData, tel: e.target.value })}
            wrapperClassName="flex-1"
          />
          <InputBox
            label="휴대전화"
            value={formData.mobile || ""}
            onChange={e => setFormData({ ...formData, mobile: e.target.value })}
            wrapperClassName="flex-1"
          />
          <InputBox
            label="팩스"
            value={formData.fax || ""}
            onChange={e => setFormData({ ...formData, fax: e.target.value })}
            wrapperClassName="flex-1"
          />
        </div>

        <div className="flex justify-between gap-2 mt-6">
          <Button variant="white" onClick={onClose} className="py-[10px]">
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            className="py-[10px]"
            disabled={saveMutation.isPending || updateMutation.isPending}
          >
            {mode === "add"
              ? saveMutation.isPending
                ? "저장 중..."
                : "저장"
              : updateMutation.isPending
                ? "수정 중..."
                : "수정"}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
